import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import { FilePlus, PaperPlaneRight, CheckCircle, XCircle, ClockCounterClockwise, ArrowsClockwise, PencilSimple } from "@phosphor-icons/react";
import { usePipeline, pipelineParams } from "../lib/pipeline";

const CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];
const STATUS_TONE = { draft: "neutral", submitted: "warning", approved: "forest", rejected: "signal", paused: "neutral" };

export default function Templates() {
  const { activePipelineSlug } = usePipeline();
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/whatsapp/templates", { params: pipelineParams(activePipelineSlug) }); setItems(data); } catch { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activePipelineSlug]);

  const submit = async (id) => {
    try { await api.post(`/whatsapp/templates/${id}/submit`); toast.success("Marcado como enviado a Meta"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const setStatus = async (id, status, rejection_reason = null) => {
    try {
      await api.post(`/whatsapp/templates/${id}/status`, { status, rejection_reason });
      toast.success(`Status → ${status}`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const syncFromMeta = async (id) => {
    try {
      const { data } = await api.post(`/whatsapp/templates/${id}/sync-from-meta`);
      toast.success(`Sync ok · Meta: ${data.meta_status} → local: ${data.local_status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error sync"); }
  };
  const del = async (id) => {
    if (!confirm("¿Eliminar template?")) return;
    await api.delete(`/whatsapp/templates/${id}`); toast.success("Eliminado"); load();
  };

  const seedBaseTemplates = async () => {
    if (!window.confirm("Crear las 19 templates base de Heroes Migrantes (founder + ambassador + globales)?\n\nIdempotente: si alguna ya existe, no la duplica.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/seed-meta-templates");
      toast.success(`OK · ${data.inserted} creadas, ${data.skipped} ya existían (${data.total_defined} total).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sembrar templates");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        label={`WhatsApp · Meta Business · ${activePipelineSlug === "all" ? "Todos los pipelines" : activePipelineSlug}`}
        title="Templates de mensaje"
        description="Templates aprobados por Meta para mensajes outbound fuera de la ventana de 24h. Crea aquí, envía a Meta Business Manager, marca aprobado cuando confirmen."
      >
        <button onClick={seedBaseTemplates} disabled={busy} data-testid="seed-base-templates-btn" className="bg-forest/10 border border-forest text-forest text-sm px-3 py-2 rounded flex items-center gap-2 hover:bg-forest/20 disabled:opacity-60">
          <ClockCounterClockwise size={14} /> Sembrar 19 base
        </button>
        <button onClick={() => setShowCreate(true)} data-testid="create-template-btn" className="bg-terracotta text-white px-3 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <FilePlus size={14} /> Nuevo template
        </button>
      </PageHeader>
      <PageBody>
        {items.length === 0 ? (
          <Card className="p-10 text-center text-carbon-400">
            <div>Aún sin templates.</div>
            <button onClick={seedBaseTemplates} disabled={busy} data-testid="seed-base-templates-empty-btn" className="mt-4 bg-terracotta text-white text-sm px-4 py-2 rounded hover:bg-terracotta-hover disabled:opacity-60">
              {busy ? "Sembrando..." : "Crear las 19 templates base de Heroes Migrantes"}
            </button>
            <div className="text-xs text-carbon-400 mt-2">Founder · Ambassador · Recordatorios · Idempotente</div>
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((t) => (
              <Card key={t.id} className="p-5" testid={`template-${t.name}`}>
                <div className="flex items-start gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-sm font-medium">{t.name}</span>
                      <Badge>{t.language}</Badge>
                      <Badge tone="terracotta">{t.category}</Badge>
                      <Badge tone={STATUS_TONE[t.status] || "neutral"}>{t.status}</Badge>
                      <Badge tone={t.pipeline_slug === "founder" ? "terracotta" : t.pipeline_slug === "ambassador" ? "forest" : "neutral"}>
                        {t.pipeline_slug ? `pipeline:${t.pipeline_slug}` : "pipeline:global"}
                      </Badge>
                      {t.meta_template_id && <span className="font-mono text-[10px] text-carbon-500">Meta: {t.meta_template_id}</span>}
                    </div>
                    {t.header_text && <div className="text-xs text-carbon-500 mb-1">Header: {t.header_text}</div>}
                    <pre className="text-sm bg-page p-3 rounded border border-carbon-200 whitespace-pre-wrap font-sans">{t.body_text}</pre>
                    {t.footer_text && <div className="text-xs text-carbon-500 mt-1">Footer: {t.footer_text}</div>}
                    {t.variables?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        <span className="text-xs text-carbon-500">Variables:</span>
                        {t.variables.map((v, i) => <Badge key={i}>{`{{${v}}}`}</Badge>)}
                      </div>
                    )}
                    {t.rejection_reason && (
                      <div className="mt-2 text-xs text-signal">Razón rechazo: {t.rejection_reason}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-44">
                    {t.status === "draft" && (
                      <>
                        <button onClick={() => setEditTemplate(t)} data-testid={`edit-${t.name}`} className="bg-page border border-carbon-200 text-carbon-700 text-xs px-3 py-1.5 rounded hover:bg-white flex items-center justify-center gap-1">
                          <PencilSimple size={12} /> Editar
                        </button>
                        <button onClick={() => submit(t.id)} data-testid={`submit-${t.name}`} className="bg-warning text-white text-xs px-3 py-1.5 rounded hover:opacity-90 flex items-center justify-center gap-1">
                          <PaperPlaneRight size={12} /> Enviar a Meta
                        </button>
                      </>
                    )}
                    {t.status === "submitted" && (
                      <>
                        <button onClick={() => syncFromMeta(t.id)} data-testid={`sync-meta-${t.name}`} className="bg-page border border-carbon-200 text-carbon-700 text-xs px-3 py-1.5 rounded hover:bg-white flex items-center justify-center gap-1">
                          <ArrowsClockwise size={12} /> Sync desde Meta
                        </button>
                        <button onClick={() => setStatus(t.id, "approved")} className="bg-forest text-white text-xs px-3 py-1.5 rounded hover:opacity-90 flex items-center justify-center gap-1">
                          <CheckCircle size={12} /> Marcar aprobado
                        </button>
                        <button onClick={() => { const r = prompt("Razón de rechazo:"); if (r) setStatus(t.id, "rejected", r); }} className="bg-signal text-white text-xs px-3 py-1.5 rounded hover:opacity-90 flex items-center justify-center gap-1">
                          <XCircle size={12} /> Marcar rechazado
                        </button>
                      </>
                    )}
                    {t.status === "approved" && (
                      <button onClick={() => setStatus(t.id, "paused")} className="bg-carbon-200 text-carbon-900 text-xs px-3 py-1.5 rounded hover:bg-carbon-300 flex items-center justify-center gap-1">
                        <ClockCounterClockwise size={12} /> Pausar
                      </button>
                    )}
                    <button onClick={() => del(t.id)} className="text-xs text-signal hover:underline">Eliminar</button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </PageBody>

      {showCreate && <CreateModal defaultPipelineSlug={activePipelineSlug === "all" ? "" : activePipelineSlug} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editTemplate && <CreateModal initial={editTemplate} onClose={() => setEditTemplate(null)} onSaved={() => { setEditTemplate(null); load(); }} />}
    </>
  );
}

function CreateModal({ defaultPipelineSlug = "", initial = null, onClose, onSaved }) {
  const isEdit = !!initial;
  const { pipelines } = usePipeline();
  const selectablePipelines = (pipelines || []).filter((p) => p.slug !== "all");
  const [form, setForm] = useState({
    name: initial?.name || "", language: initial?.language || "es", category: initial?.category || "UTILITY",
    body_text: initial?.body_text || "", header_text: initial?.header_text || "", footer_text: initial?.footer_text || "",
    variables: (initial?.variables || []).join(", "),
    pipeline_slug: initial ? (initial.pipeline_slug || "") : (defaultPipelineSlug || ""),
  });
  const [busy, setBusy] = useState(false);
  const [availableVars, setAvailableVars] = useState([]);
  const [activeField, setActiveField] = useState("body_text");
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  useEffect(() => {
    api.get("/whatsapp/template-variables")
      .then((r) => setAvailableVars(r.data?.variables || []))
      .catch(() => setAvailableVars([]));
  }, []);

  const insertVar = (key) => {
    const placeholder = `{{${key}}}`;
    const refMap = { body_text: bodyRef, header_text: headerRef, footer_text: footerRef };
    const ref = refMap[activeField];
    const el = ref?.current;
    if (!el) {
      // fallback: append al final
      setForm((f) => ({ ...f, [activeField]: (f[activeField] || "") + placeholder }));
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const current = form[activeField] || "";
    const next = current.substring(0, start) + placeholder + current.substring(end);
    setForm((f) => ({ ...f, [activeField]: next }));
    // Reposicionar cursor después del insert
    setTimeout(() => {
      try {
        el.focus();
        el.setSelectionRange(start + placeholder.length, start + placeholder.length);
      } catch (_) { /* noop */ }
    }, 0);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const variables = form.variables.split(",").map((v) => v.trim()).filter(Boolean);
      if (isEdit) {
        await api.patch(`/whatsapp/templates/${initial.id}`, {
          body_text: form.body_text,
          header_text: form.header_text,
          footer_text: form.footer_text,
          category: form.category,
          variables,
          pipeline_slug: form.pipeline_slug || null,
        });
        toast.success("Template actualizado");
      } else {
        await api.post("/whatsapp/templates", {
          ...form,
          variables,
          pipeline_slug: form.pipeline_slug || null,
        });
        toast.success("Template creado en draft");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-4xl max-h-[92vh] overflow-hidden flex">
        {/* Panel principal */}
        <div className="flex-1 p-6 overflow-y-auto scrollbar-thin">
          <div className="section-label mb-1">WhatsApp Template</div>
          <h2 className="font-heading text-2xl mb-1">{isEdit ? "Editar template" : "Nuevo template"}</h2>
          <p className="text-xs text-carbon-500 mb-4">{isEdit ? "Editá el contenido mientras el template siga en draft (antes de enviarlo a Meta). El nombre y el idioma no se pueden cambiar." : "Después de crear, envíalo a Meta y marca el status según el feedback."}</p>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <label className="block col-span-2"><span className="section-label">Nombre (lowercase, _)</span>
                <input required pattern="[a-z0-9_]+" disabled={isEdit} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="tpl-name" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono disabled:opacity-60 disabled:cursor-not-allowed" /></label>
              <label className="block"><span className="section-label">Idioma</span>
                <input required disabled={isEdit} value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono disabled:opacity-60 disabled:cursor-not-allowed" /></label>
            </div>
            <label className="block"><span className="section-label">Categoría</span>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </label>
            <label className="block"><span className="section-label">Pipeline · aislamiento por programa</span>
              <select value={form.pipeline_slug} onChange={(e) => setForm({ ...form, pipeline_slug: e.target.value })} data-testid="tpl-pipeline" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">Global (todos los pipelines)</option>
                {selectablePipelines.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.short || p.label}</option>
                ))}
              </select>
              <span className="text-[10px] text-carbon-500 mt-1 block">Si seleccionas un pipeline, el template solo será visible cuando ese pipeline esté activo.</span>
            </label>
            <label className="block"><span className="section-label">Header (opcional)</span>
              <input ref={headerRef} value={form.header_text} onFocus={() => setActiveField("header_text")}
                onChange={(e) => setForm({ ...form, header_text: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Cuerpo</span>
              <textarea ref={bodyRef} required rows={5} value={form.body_text} onFocus={() => setActiveField("body_text")}
                onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                data-testid="tpl-body"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Footer (opcional)</span>
              <input ref={footerRef} value={form.footer_text} onFocus={() => setActiveField("footer_text")}
                onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" /></label>
            <label className="block"><span className="section-label">Variables Meta (separadas por coma)</span>
              <input value={form.variables} onChange={(e) => setForm({ ...form, variables: e.target.value })} placeholder="name, city" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
              <span className="text-[10px] text-carbon-500 mt-1 block">Para registrar en Meta el orden de las variables {`{{1}}, {{2}}, ...`}. Las del panel derecho funcionan en runtime para todos los outbounds.</span>
            </label>
            <div className="flex justify-end gap-2 pt-3 border-t border-carbon-200">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-carbon-700 hover:bg-carbon-100 rounded">Cancelar</button>
              <button type="submit" disabled={busy} data-testid="tpl-submit" className="px-4 py-2 text-sm bg-terracotta text-white rounded hover:bg-terracotta-hover disabled:opacity-60">{busy ? (isEdit ? "Guardando..." : "Creando...") : (isEdit ? "Guardar cambios" : "Crear draft")}</button>
            </div>
          </form>
        </div>

        {/* Panel lateral · Variables disponibles */}
        <aside className="w-72 bg-page border-l border-carbon-200 p-4 overflow-y-auto scrollbar-thin" data-testid="tpl-vars-panel">
          <div className="section-label mb-2">Variables disponibles</div>
          <p className="text-[11px] text-carbon-500 mb-3 leading-relaxed">
            Click en cualquier variable para insertarla en el campo activo (<strong className="font-mono">{activeField.replace("_text", "")}</strong>). Se reemplazan al enviar por los datos reales del lead. Si el campo está vacío en el lead, se sustituye por nada.
          </p>
          <div className="space-y-1">
            {availableVars.map((v) => (
              <button key={v.key} type="button" onClick={() => insertVar(v.key)}
                data-testid={`var-insert-${v.key}`}
                className="w-full text-left p-2 border border-carbon-200 rounded hover:border-terracotta hover:bg-surface transition-colors group">
                <div className="font-mono text-xs text-terracotta">{`{{${v.key}}}`}</div>
                <div className="text-[11px] text-carbon-600 mt-0.5">{v.label}</div>
                <div className="text-[10px] text-carbon-400 italic">ej: {v.example}</div>
              </button>
            ))}
          </div>
        </aside>
      </div>
    </div>
  );
}
